"use client";

import React, { useState } from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { stegaClean } from "@sanity/client/stega";
import { richTextComponent } from "@/sanity/sanityPortableText";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DotsBackgroundDark from "@/components/career/DotsBackgroundDark";
import { CTACornerButton } from "@/shadcn/ui/corner-button";

import { PageCustomersSchema } from "@/sanity/pages/PageCustomersTemplate";

import checkBlack from "@/public/customers/check-black.svg";
import checkWhite from "@/public/customers/check-white.svg";
import close from "@/public/customers/close.svg";
import toggleHint from "@/public/customers/toggle-hint.svg";
import toggleHintMobile from "@/public/customers/toggle-hint-mobile.svg";

type Plan = PageCustomersSchema["pricingPlans"][number];

const fadeInUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" as const },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay },
});

/**
 * Both cards share the same silhouette: a rectangle with the bottom-right
 * corner sliced off and a small loose triangle sitting in the cut. The
 * clip-path removes the diagonal edge of the border, so the free card draws
 * that edge back in with a rotated hairline.
 */
function CardShape({ highlighted }: { highlighted?: boolean }) {
  return (
    <>
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 [clip-path:polygon(0_0,100%_0,100%_calc(100%-1.6rem),calc(100%-1.6rem)_100%,0_100%)]",
          highlighted ? "bg-white" : "border border-white/10 bg-white/[.02]",
        )}
      />
      {!highlighted && (
        <div
          aria-hidden
          className="pointer-events-none absolute right-0 bottom-0 h-px w-[2.25rem] translate-x-[.325rem] -translate-y-[.8rem] -rotate-45 bg-white/10"
        />
      )}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute right-[.0625rem] bottom-[.0625rem] h-[1.3rem] w-[1.3rem] [clip-path:polygon(100%_0,100%_100%,0_100%)]",
          highlighted ? "bg-white" : "bg-white/10",
        )}
      />
    </>
  );
}

function PeriodToggle({
  data,
  yearly,
  setYearly,
  variant = "dark",
  className,
}: {
  data: PageCustomersSchema;
  yearly: boolean;
  setYearly: (value: boolean) => void;
  variant?: "dark" | "light";
  className?: string;
}) {
  const light = variant === "light";

  const tab = cn(
    "flex items-center justify-center rounded-[1rem] border border-transparent px-[2.5rem] py-[1.25rem] font-geist font-medium leading-normal whitespace-nowrap transition-colors",
    light ? "text-[2rem] text-black" : "text-[2.25rem] text-white",
  );

  const active = light ? "border-black/[.12] bg-[#D9F9B9]" : "border-[#D9F9B9]/50 bg-[#D9F9B9]/[.14] text-[#D9F9B9]";

  return (
    <div
      className={cn(
        "flex items-center gap-[.5rem] rounded-[1.5rem] border p-[.75rem]",
        light ? "w-full border-black/[.16]" : "w-fit border-white/[.16]",
        className,
      )}>
      <button
        type="button"
        onClick={() => setYearly(false)}
        aria-pressed={!yearly}
        className={cn(tab, light && "flex-1", !yearly && active)}>
        {data?.pricingTabMonthly || "Monatlich"}
      </button>

      <button
        type="button"
        onClick={() => setYearly(true)}
        aria-pressed={yearly}
        className={cn(tab, "self-stretch", yearly && active)}>
        {data?.pricingTabYearly || "Jährlich"}
        {data?.pricingTabYearlyNote && (
          <span className={cn("ml-[1rem]", !light && "text-[#D9F9B9]")}>{data.pricingTabYearlyNote}</span>
        )}
      </button>
    </div>
  );
}

function FeatureRow({ feature, highlighted }: { feature: Plan["features"][number]; highlighted?: boolean }) {
  const included = feature?.included !== false;

  return (
    <div className="flex w-full items-start gap-[1.5rem]">
      <span
        className={cn(
          "flex shrink-0 items-center justify-center overflow-clip",
          highlighted ? "p-[.75rem]" : "p-[.25rem] lg:p-[.75rem]",
          included
            ? highlighted
              ? "bg-[#0A1A1D]"
              : "bg-[#D9F9B9]"
            : highlighted
              ? "bg-[#0A1A1D]/[.08]"
              : "bg-white/[.08]",
        )}>
        <Image
          className={cn("!h-[2rem] !w-[2rem]", !included && highlighted && "invert")}
          src={included ? (highlighted ? checkWhite : checkBlack) : close}
          alt=""
          aria-hidden
        />
      </span>
      <p
        className={cn(
          "m-0 flex-1 font-geist text-[2rem] leading-[1.5] font-normal",
          highlighted
            ? included
              ? "text-[#0A1A1D]"
              : "text-[#0A1A1D]/[.38]"
            : included
              ? "text-white/90"
              : "text-white/[.38]",
        )}>
        {feature?.text}
      </p>
    </div>
  );
}

function PlanCard({
  plan,
  data,
  yearly,
  setYearly,
  index,
}: {
  plan: Plan;
  data: PageCustomersSchema;
  yearly: boolean;
  setYearly: (value: boolean) => void;
  index: number;
}) {
  const highlighted = Boolean(plan?.highlighted);

  const price = (yearly ? plan?.priceYearly : plan?.priceMonthly) || plan?.priceMonthly;

  const switchesPeriod = Boolean(plan?.priceYearly) && stegaClean(plan?.priceYearly) !== stegaClean(plan?.priceMonthly);

  // the free card tightens its rhythm on mobile; the upgrade card keeps the desktop spacing
  const stack = highlighted ? "gap-[2rem]" : "gap-[1.25rem] lg:gap-[2rem]";

  return (
    <motion.div
      {...fadeInUp(0.15 + index * 0.1)}
      className={cn(
        "relative flex flex-1 flex-col justify-between px-[3rem] pb-[3rem] lg:min-h-[94.75rem] lg:pt-[5rem]",
        highlighted ? "pt-[5rem]" : "pt-[3.5rem]",
      )}>
      <CardShape highlighted={highlighted} />

      {plan?.ribbon && (
        <div className="absolute -top-[4.375rem] left-0 flex h-[4.375rem] items-center bg-[#D9F9B9] px-[2rem] [clip-path:polygon(0_0,calc(100%-.875rem)_0,100%_.875rem,100%_100%,0_100%)]">
          <p className="m-0 font-geistmono text-[1.75rem] leading-none font-medium text-black">{plan.ribbon}</p>
        </div>
      )}

      <div className="relative flex w-full flex-col gap-[3rem] lg:gap-[4rem]">
        <div className={cn("flex w-full flex-col", stack)}>
          <p
            className={cn(
              "m-0 font-geist text-[2rem] leading-normal font-medium",
              highlighted ? "text-[#0A1A1D]/50" : "text-white",
            )}>
            {plan?.name}
          </p>

          <div className={cn("flex w-full flex-col", stack)}>
            <div className="flex w-full flex-col items-start gap-[1.5rem] pt-[2rem] lg:flex-row lg:items-end">
              <div className="flex items-end gap-[1.5rem]">
                {/* text-box-trim matches the Figma cap-height bounds so the suffix
                    and badge sit on the price baseline instead of its descender */}
                <p
                  className={cn(
                    "m-0 font-funnel text-[6rem] leading-normal font-medium tracking-[-0.02em] whitespace-nowrap [text-box-edge:cap_alphabetic] [text-box-trim:trim-both] lg:text-[9rem]",
                    highlighted ? "text-[#0A1A1D]" : "text-white",
                  )}>
                  {price}
                </p>

                {plan?.priceSuffix && (
                  <p
                    className={cn(
                      "m-0 font-geist text-[2rem] leading-normal font-normal whitespace-nowrap",
                      highlighted ? "text-[#0A1A1D]/45" : "text-white/45",
                    )}>
                    {plan.priceSuffix}
                  </p>
                )}
              </div>

              {plan?.badge && (
                <div
                  className={cn(
                    "flex h-[3.5rem] shrink-0 items-center justify-center px-[1.5rem]",
                    highlighted ? "bg-[#0A1A1D] text-white" : "bg-[#D9F9B9] text-black",
                  )}>
                  <p className="m-0 font-geist text-[1.5rem] leading-normal font-medium whitespace-nowrap">
                    {plan.badge}
                  </p>
                </div>
              )}
            </div>

            {plan?.note && (
              <p
                className={cn(
                  "m-0 font-geist text-[2rem] leading-[1.5] font-normal",
                  highlighted ? "text-[#0A1A1D]/[.62]" : "text-white/60",
                )}>
                {plan.note}
              </p>
            )}

            {switchesPeriod && (
              <div className="relative w-full lg:hidden">
                <PeriodToggle
                  variant={highlighted ? "light" : "dark"}
                  data={data}
                  yearly={yearly}
                  setYearly={setYearly}
                />

                {highlighted && (
                  /* the mobile note sits above the toggle, its arrow pointing
                     down at the yearly tab; offsets account for the empty
                     margin inside the exported SVG canvas */
                  <Image
                    aria-hidden
                    className="pointer-events-none absolute -right-[3.5rem] bottom-[calc(100%+.125rem)] !h-[14.375rem] !w-[14.75rem] max-w-none"
                    src={toggleHintMobile}
                    alt=""
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <div className={cn("h-px w-full", highlighted ? "bg-[#0A1A1D]/[.16]" : "bg-white/[.12]")} />

        {plan?.features?.length > 0 && (
          <div className={cn("flex w-full flex-col", stack)}>
            {plan.features.map((feature, index) => (
              <FeatureRow
                key={index}
                feature={feature}
                highlighted={highlighted}
              />
            ))}
          </div>
        )}
      </div>

      {/* only the upgrade card carries a button; the free plan ends on its list */}
      {highlighted && plan?.cta && (
        <div className="relative mt-[4rem] w-full">
          <CTACornerButton
            className="!block w-full min-w-0 sm:w-full [&_.corner-button]:min-h-[7rem] [&_.corner-button]:!bg-[#0A1A1D] [&_.corner-button]:px-[4rem] [&_.corner-button]:text-[2.25rem] [&_.corner-button_svg]:!text-white [&_.corner-button::before]:!bg-white"
            data={{ type: "dark", cta: plan.cta }}
          />
        </div>
      )}
    </motion.div>
  );
}

export default function PricingSection({ data, className }: { data: PageCustomersSchema; className?: string }) {
  const [yearly, setYearly] = useState(false);

  const plans = data?.pricingPlans ?? [];

  // the toggle only earns its place once a plan actually prices a second period
  const hasYearly = plans.some((plan) => plan?.priceYearly);

  return (
    <section
      id="customers-content"
      className={cn(
        "relative overflow-clip bg-[#0A1A1D] px-[2rem] py-[10rem] sm:px-[3rem] md:px-[5rem] lg:py-[15rem]",
        className,
      )}>
      <DotsBackgroundDark className="absolute top-0 left-1/2 -translate-x-1/2" />

      <DecorationLine
        className="top-0 left-[1rem] lg:left-[8.85%]"
        orientation="vertical"
        style="light"
      />
      <DecorationLine
        className="top-0 right-[1rem] lg:right-[8.85%]"
        orientation="vertical"
        style="light"
      />

      <div className="relative z-[1] mx-auto flex w-[min(149.75rem,100%)] flex-col items-center gap-[4.25rem]">
        <div className="flex w-[min(123.5rem,100%)] flex-col items-center gap-[2.375rem]">
          {data?.pricingChip?.text && (
            <motion.div {...fadeInUp()}>
              <Chip
                className="mb-0 [&_img]:!w-[2rem] [&_p]:font-geistmono [&_p]:text-[#202223] [&_p]:uppercase"
                image={data?.pricingChip?.icon}
                text={data?.pricingChip?.text}
              />
            </motion.div>
          )}

          {data?.pricingTitle && (
            <motion.div
              {...fadeInUp(0.1)}
              className="richtext richtext-center w-[min(67rem,100%)] prose-em:font-ivy prose-em:text-[#D9F9B9] prose-headings:mb-0 prose-headings:text-[4.5rem] prose-headings:leading-none prose-headings:text-white lg:prose-headings:text-[6.5rem]">
              <PortableText
                value={data.pricingTitle}
                components={richTextComponent}
              />
            </motion.div>
          )}

          {data?.pricingText && (
            <motion.div
              {...fadeInUp(0.2)}
              className="richtext richtext-center w-[min(86.5rem,100%)] prose-p:mb-0 prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-white/60">
              <PortableText
                value={data.pricingText}
                components={richTextComponent}
              />
            </motion.div>
          )}
        </div>

        <div className="flex w-full flex-col items-center gap-[2rem] lg:gap-[9rem]">
          {hasYearly && (
            <motion.div
              {...fadeInUp(0.1)}
              className="relative hidden lg:block">
              <PeriodToggle
                data={data}
                yearly={yearly}
                setYearly={setYearly}
              />

              {/* the handwritten "save 50%" aside is outlined text exported
                  from Figma, hanging off the toggle's right edge */}
              <Image
                aria-hidden
                className="pointer-events-none absolute top-[-.75rem] left-[calc(100%+2rem)] !h-[9rem] !w-[19rem] max-w-none"
                src={toggleHint}
                alt=""
              />
            </motion.div>
          )}

          {plans.length > 0 && (
            <div className="flex w-full flex-col items-stretch gap-[9rem] lg:flex-row lg:gap-[3rem]">
              {plans.map((plan, index) => (
                <PlanCard
                  key={index}
                  plan={plan}
                  data={data}
                  yearly={yearly}
                  setYearly={setYearly}
                  index={index}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
